import { useState, useEffect, useMemo, useCallback } from "react";
import { API, axios } from "@/api";
import { Calendar as CalendarIcon, X, ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "sonner";

export default function VendorAvailabilityCalendar() {
  const [unavailableDates, setUnavailableDates] = useState([]);
  const [reasons, setReasons] = useState({});
  const [currentMonth, setCurrentMonth] = useState(() => {
    const now = new Date();
    return { year: now.getFullYear(), month: now.getMonth() };
  });
  const [saving, setSaving] = useState(false);
  const [editingDate, setEditingDate] = useState(null);
  const [reasonInput, setReasonInput] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/vendor/availability`);
        setUnavailableDates(res.data?.unavailable_dates || []);
        setReasons(res.data?.unavailable_reason || {});
      } catch {}
    })();
  }, []);

  const today = useMemo(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  }, []);

  const maxDate = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() + 30);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  }, []);

  const daysInMonth = useMemo(() => {
    return new Date(currentMonth.year, currentMonth.month + 1, 0).getDate();
  }, [currentMonth]);

  const firstDayOfWeek = useMemo(() => {
    return new Date(currentMonth.year, currentMonth.month, 1).getDay();
  }, [currentMonth]);

  const toggleDate = (dateStr) => {
    if (dateStr < today || dateStr > maxDate) return;

    if (unavailableDates.includes(dateStr)) {
      setUnavailableDates((prev) => prev.filter((d) => d !== dateStr));
      setReasons((prev) => { const r = { ...prev }; delete r[dateStr]; return r; });
    } else {
      setUnavailableDates((prev) => [...prev, dateStr]);
      setEditingDate(dateStr);
      setReasonInput("");
    }
  };

  const saveReason = () => {
    if (editingDate && reasonInput.trim()) {
      setReasons((prev) => ({ ...prev, [editingDate]: reasonInput.trim().slice(0, 40) }));
    }
    setEditingDate(null);
    setReasonInput("");
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/vendor/availability`, {
        unavailable_dates: unavailableDates,
        unavailable_reason: reasons,
      });
      toast.success("Availability saved");
    } catch {
      toast.error("Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const prevMonth = () => {
    setCurrentMonth((prev) => {
      if (prev.month === 0) return { year: prev.year - 1, month: 11 };
      return { ...prev, month: prev.month - 1 };
    });
  };

  const nextMonth = () => {
    setCurrentMonth((prev) => {
      if (prev.month === 11) return { year: prev.year + 1, month: 0 };
      return { ...prev, month: prev.month + 1 };
    });
  };

  const monthLabel = new Date(currentMonth.year, currentMonth.month).toLocaleDateString("en-US", { month: "long", year: "numeric" });

  return (
    <div className="space-y-4" data-testid="vendor-availability-calendar">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-semibold text-white flex items-center gap-2">
          <CalendarIcon className="w-4 h-4 text-[#F59E0B]" /> Availability Calendar
        </h3>
      </div>

      {/* Month nav */}
      <div className="flex items-center justify-between">
        <button onClick={prevMonth} className="p-1.5 rounded-lg bg-white/5 hover:bg-white/10" data-testid="prev-month">
          <ChevronLeft className="w-4 h-4 text-white/60" />
        </button>
        <span className="text-sm font-medium text-white">{monthLabel}</span>
        <button onClick={nextMonth} className="p-1.5 rounded-lg bg-white/5 hover:bg-white/10" data-testid="next-month">
          <ChevronRight className="w-4 h-4 text-white/60" />
        </button>
      </div>

      {/* Calendar grid */}
      <div className="grid grid-cols-7 gap-1 text-center">
        {["S", "M", "T", "W", "T", "F", "S"].map((d, i) => (
          <div key={i} className="text-[10px] text-white/30 py-1">{d}</div>
        ))}
        {Array.from({ length: firstDayOfWeek }).map((_, i) => (
          <div key={`empty-${i}`} />
        ))}
        {Array.from({ length: daysInMonth }).map((_, i) => {
          const day = i + 1;
          const dateStr = `${currentMonth.year}-${String(currentMonth.month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
          const isPast = dateStr < today;
          const isTooFar = dateStr > maxDate;
          const isUnavailable = unavailableDates.includes(dateStr);
          const isToday = dateStr === today;
          const disabled = isPast || isTooFar;

          return (
            <button
              key={day}
              onClick={() => !disabled && toggleDate(dateStr)}
              disabled={disabled}
              className={`relative w-full aspect-square rounded-lg text-xs font-medium flex items-center justify-center transition-colors
                ${isUnavailable ? "bg-red-500/20 text-red-400 border border-red-500/30" : ""}
                ${isToday && !isUnavailable ? "border border-[#F59E0B]/50 text-[#F59E0B]" : ""}
                ${!isUnavailable && !isToday && !disabled ? "text-white/70 hover:bg-white/10" : ""}
                ${disabled ? "text-white/15 cursor-not-allowed" : ""}
              `}
              data-testid={`cal-day-${dateStr}`}
            >
              {day}
              {isUnavailable && (
                <X className="absolute w-3 h-3 text-red-400 top-0.5 right-0.5" />
              )}
            </button>
          );
        })}
      </div>

      {/* Unavailable dates list */}
      {unavailableDates.length > 0 && (
        <div className="space-y-1">
          <p className="text-[10px] text-white/30 uppercase tracking-wider">Unavailable Dates</p>
          {unavailableDates.sort().map((d) => (
            <div key={d} className="flex items-center justify-between bg-red-500/5 rounded-lg px-3 py-1.5">
              <div>
                <span className="text-xs text-red-400 font-medium">{d}</span>
                {reasons[d] && <span className="text-[10px] text-white/40 ml-2">{reasons[d]}</span>}
              </div>
              <button onClick={() => {
                setUnavailableDates((prev) => prev.filter((x) => x !== d));
                setReasons((prev) => { const r = { ...prev }; delete r[d]; return r; });
              }} className="p-1">
                <X className="w-3 h-3 text-red-400/60" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Reason input modal */}
      {editingDate && (
        <div className="bg-[#3C3C3E] rounded-xl p-3 space-y-2">
          <p className="text-xs text-white/60">Reason for {editingDate} (optional, max 40 chars)</p>
          <input value={reasonInput} onChange={(e) => setReasonInput(e.target.value.slice(0, 40))}
            placeholder="e.g. Truck maintenance"
            className="w-full bg-[#1C1C1E] rounded-lg px-3 py-2 text-sm text-white border border-white/10 focus:border-[#F59E0B]/50 outline-none"
            data-testid="reason-input" />
          <div className="flex gap-2">
            <button onClick={saveReason} className="flex-1 bg-[#F59E0B] text-black rounded-lg py-1.5 text-xs font-medium" data-testid="save-reason-btn">
              {reasonInput.trim() ? "Save Reason" : "Skip"}
            </button>
            <button onClick={() => { setEditingDate(null); setReasonInput(""); }} className="px-3 text-white/40 text-xs">Cancel</button>
          </div>
        </div>
      )}

      {/* Save button */}
      <button onClick={handleSave} disabled={saving}
        className="w-full bg-[#F59E0B] text-black font-semibold rounded-xl py-3 text-sm disabled:opacity-50"
        data-testid="save-availability-btn">
        {saving ? "Saving..." : "Save Availability"}
      </button>
    </div>
  );
}
